import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../../common/types';
import { SMLButton } from '../../components/primitives/SMLButton';
import { LockedControl } from '../../components/LockedControl/LockedControl';
import { SerializedSocialChannel, SocialLoginVariant, SocialPlatform } from '../types';
import { ChannelStatusMark } from './ChannelStatusMark';
import { InstagramLoginAction } from './InstagramLoginAction';
import { ChannelDisconnectButton } from './ChannelDisconnectButton';
import { useMusic24Capabilities } from '../../hooks/useMusic24Capabilities';
import { useUpgradePrompt } from '../../hooks/useUpgradePrompt';
import { SMLUpgradeFeature } from '../../config/music24PlanCapabilities';
import { planNameMessages } from '../../config/music24PlanNames';
import { xChannelNeedsPro, xConnectLocked } from '../xPostsLimit';
import { ChannelPartnerNote } from './ChannelPartnerNote';
import { channelConnectNeedsPaidPlan } from './channelConnectRoute';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole Promote pillar; each part uses its own slice of it */
import styles from './PromoteChannels.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface ChannelCardActionsProps {
  channel: SerializedSocialChannel;
  isSaving: boolean;
  /** How many of the workspace's channels already hold a platform identity — the count the
   *  server's own `M24_LIMIT_SOCIAL` check charges against. */
  connectedCount: number;
  /** Opens the handle field. The manual path: you publish the post yourself. */
  onEditHandle: () => void;
  /** Clears the handle, which turns the manual channel off. */
  onRemoveHandle: (platform: SocialPlatform) => void;
  /** Starts the OAuth login, which lets us post for you. `loginVariant` is the second login of Instagram. */
  onConnect: (platform: SocialPlatform, loginVariant?: SocialLoginVariant) => void;
  /** Drops the tokens and falls back to the manual path. */
  onDisconnectConnection: (platform: SocialPlatform) => void;
}

/** The two paths a card offers: let us post, or post it yourself. */
const ChannelCardActionsComponent: React.FC<ChannelCardActionsProps> = ({
  channel,
  isSaving,
  connectedCount,
  onEditHandle,
  onRemoveHandle,
  onConnect,
  onDisconnectConnection,
}) => {
  const intl = useIntl();
  const hasHandle = channel.handle !== null && channel.handle !== '';
  const { platform, connection } = channel;
  const { autoPublishAvailable } = channel;
  const { socialAccountsLimit, plan, canUseX } = useMusic24Capabilities();
  const openUpgradePrompt = useUpgradePrompt();
  // X on a plan without X (Free, Artist, the trial): the handle path stays, the connect is locked
  // and opens the X prompt (design x-connector a1, a3).
  const isXLocked = xConnectLocked(channel, canUseX);
  const needsPro = xChannelNeedsPro(channel, canUseX);

  const removeHandle = useCallback(() => onRemoveHandle(platform), [onRemoveHandle, platform]);
  const connect = useCallback(() => onConnect(platform), [onConnect, platform]);
  const openSocialAccountsPrompt = useCallback(
    () => openUpgradePrompt(SMLUpgradeFeature.socialAccounts),
    [openUpgradePrompt]
  );
  const openXPrompt = useCallback(() => openUpgradePrompt(SMLUpgradeFeature.x), [openUpgradePrompt]);
  const openPlanPrompt = useCallback(() => openUpgradePrompt(SMLUpgradeFeature.generic), [openUpgradePrompt]);

  // Read by both the locked control and the plain connect button — never re-declared per branch.
  const connectAutoLabel = intl.formatMessage({
    id: 'app.sml.promote.channel.connectAuto',
    defaultMessage: 'Connect for auto-posting',
  });

  const reconnectLabel = intl.formatMessage({ id: 'app.sml.promote.channel.reconnect', defaultMessage: 'Reconnect' });

  // A channel that already holds a connection never locks its own reconnect — the count only
  // stops a NEW one from being added past the plan's slot.
  const isAtSocialLimit =
    !isXLocked && socialAccountsLimit !== null && connectedCount >= socialAccountsLimit && connection === null;

  // The platform ended the sign-in: the warn line of the card starts the connect flow, so the
  // footer has no second Reconnect control (design social-connectors a1).
  const hasReconnectLine = channel.syncStatus === 'reauthRequired';

  // The server says a new connect goes to the posting partner, which needs a paid plan: the
  // button keeps its label, gets the plan lock and opens the plan offer (no login starts).
  const needsPaidPlan = channelConnectNeedsPaidPlan(channel) && !isXLocked && !isAtSocialLimit;

  // The second login of Instagram, for an account with no Facebook Page. The server says when it
  // can start it. A connected channel and a channel at the plan limit do not show it.
  const offersInstagramLogin =
    channel.alternativeLogin === 'instagram' &&
    channel.status !== 'connected' &&
    autoPublishAvailable &&
    !isAtSocialLimit;

  // A new Instagram connect offers both logins as two buttons (owner, 2026-10-07): most artists
  // sign in with Instagram itself, so it leads; the Facebook Page login is the second button.
  const isInstagramChoice = offersInstagramLogin && connection === null && !hasReconnectLine && !needsPaidPlan;
  // The plain connect button: not on the X lock, not at the account limit, not next to the warn line.
  const offersConnect =
    autoPublishAvailable && !isXLocked && !isAtSocialLimit && !hasReconnectLine && !isInstagramChoice;
  const signInWithInstagram = useCallback(() => onConnect('instagram', 'instagram'), [onConnect]);

  const manualButton = hasHandle ? (
    <SMLButton disabled={isSaving} onClick={removeHandle}>
      {intl.formatMessage({ id: 'app.sml.promote.channel.removeHandle', defaultMessage: 'Remove handle' })}
    </SMLButton>
  ) : (
    <SMLButton variant={autoPublishAvailable ? 'default' : 'primary'} disabled={isSaving} onClick={onEditHandle}>
      {autoPublishAvailable
        ? intl.formatMessage({ id: 'app.sml.promote.channel.handleOnly', defaultMessage: 'Use a handle only' })
        : intl.formatMessage({ id: 'app.sml.promote.channel.addHandle', defaultMessage: 'Add handle' })}
    </SMLButton>
  );

  return (
    <>
      <div className={styles.foot}>
        <ChannelStatusMark channel={channel} needsPro={needsPro} />
        <div className={styles.footActions}>
          {channel.status === 'connected' ? (
            <ChannelDisconnectButton platform={platform} isSaving={isSaving} onConfirm={onDisconnectConnection} />
          ) : (
            <>
              {manualButton}
              {autoPublishAvailable && isAtSocialLimit ? (
                <LockedControl
                  variant="button"
                  feature={SMLUpgradeFeature.socialAccounts}
                  onClick={openSocialAccountsPrompt}
                  label={connectAutoLabel}
                  disabled={isSaving}
                  // The unlocked sibling below is the plain `primitives/SMLButton`, which never
                  // title-cases its label — keep the two states reading the same word for word.
                  omitCap
                />
              ) : null}
              {autoPublishAvailable && isXLocked ? (
                <LockedControl
                  variant="button"
                  feature={SMLUpgradeFeature.x}
                  onClick={openXPrompt}
                  label={connectAutoLabel}
                  disabled={isSaving}
                  omitCap
                />
              ) : null}
              {offersConnect && needsPaidPlan ? (
                <LockedControl
                  variant="button"
                  feature={SMLUpgradeFeature.generic}
                  onClick={openPlanPrompt}
                  label={connection === null ? connectAutoLabel : reconnectLabel}
                  disabled={isSaving}
                  omitCap
                />
              ) : null}
              {offersConnect && !needsPaidPlan ? (
                <SMLButton variant="primary" disabled={isSaving} onClick={connect}>
                  {connection === null ? connectAutoLabel : reconnectLabel}
                </SMLButton>
              ) : null}
            </>
          )}
        </div>
      </div>
      {isInstagramChoice ? (
        <div className={styles.connectChoices}>
          <SMLButton variant="primary" disabled={isSaving} onClick={signInWithInstagram}>
            {intl.formatMessage({
              id: 'app.sml.promote.channels.instagram.signIn',
              defaultMessage: 'Sign in with Instagram',
            })}
          </SMLButton>
          <SMLButton disabled={isSaving} onClick={connect}>
            {intl.formatMessage({
              id: 'app.sml.promote.channels.instagram.facebookPage',
              defaultMessage: 'Connect a Facebook Page',
            })}
          </SMLButton>
        </div>
      ) : null}
      {offersInstagramLogin && !isInstagramChoice ? (
        <InstagramLoginAction
          label={intl.formatMessage({
            id: 'app.sml.promote.channels.instagramLogin.action',
            defaultMessage: 'No Facebook Page? Sign in with Instagram',
          })}
          disabled={isSaving}
          onConnect={onConnect}
        />
      ) : null}
      {autoPublishAvailable && (channel.status !== 'connected' || hasReconnectLine) ? (
        <ChannelPartnerNote channel={channel} />
      ) : null}
      {autoPublishAvailable && isAtSocialLimit ? (
        <p className={styles.limitCaption}>
          {intl.formatMessage(
            {
              id: 'app.sml.promote.channels.limit',
              defaultMessage: '{used} of {limit} accounts on {plan}',
            },
            {
              used: connectedCount,
              limit: socialAccountsLimit,
              plan: intl.formatMessage(planNameMessages[plan]),
            }
          )}
        </p>
      ) : null}
    </>
  );
};

export const ChannelCardActions = TypedMemo(ChannelCardActionsComponent);
